import { useDraftGuard } from '@/lib/use-draft-guard';
import { Suspense, lazy, useEffect, useId, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  BookUser,
  ChevronDown,
  ChevronRight,
  CreditCard,
  HardDrive,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageCircle,
  MonitorDown,
  Moon,
  Phone,
  Search,
  UserRound,
  ShieldOff,
  Sparkles,
  Sun,
  Users,
  Webhook,
  Plug,
} from 'lucide-react';
import { useWorkspace } from '@/app/workspace';
import { useAuth } from '@/app/auth';
import { useTheme } from '@/app/theme';
import { useSetupProgress } from '@/app/setup';
import { useAudio } from '@/app/audio';
import { useSupportChat } from '@/app/support-chat';
import { LogoMark } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { Avatar, Badge, Progress, Skeleton } from '@/components/ui/misc';
import { Drawer, Notice } from '@/components/ui/system';
import { ContactDrawer } from '@/components/domain/contact-drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/overlay';
import { CommandMenu } from './command-menu';
import { AudioBar } from './audio-bar';
import { InstallAppDialog } from '@/components/install-app';
import { config } from '@/lib/config';
import { rememberSetupDone, runningAsApp, useInstallPrompt } from '@/lib/install-app';
import { dataSourceUrl } from '@/lib/data-source';
const DemoScenarioSwitch = lazy(() => import('./demo-scenario-switch'));
import { formatDate } from '@/lib/format';
import { formatStorageGb, seatPriceLabel, showPayNowBanner } from '@shared/pricing';
import { billingCurrencyForCountry } from '@shared/countries';
import { cn, storage } from '@/lib/utils';
import { preloadPage } from '@/lib/page-loaders';
export const NAV_SECTIONS = [
  {
    title: 'Workspace',
    items: [
      { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
      { to: '/calls', label: 'Calls', icon: Phone },
      { to: '/contacts', label: 'Contacts', icon: BookUser },
      { to: '/team', label: 'Team', icon: Users },
    ],
  },
  {
    title: 'Manage',
    items: [
      { to: '/exclusions', label: 'Excluded numbers', icon: ShieldOff },
      { to: '/ai', label: 'AI analysis', icon: Sparkles },
      { to: '/api-apps', label: 'API & Apps', icon: Plug },
      { to: '/webhooks', label: 'Webhooks', icon: Webhook },
      { to: '/recordings', label: 'Recording storage', icon: HardDrive },
      { to: '/billing', label: 'Plan & billing', icon: CreditCard },
    ],
  },
  {
    title: 'Account',
    items: [{ to: '/account', label: 'Account', icon: UserRound }],
  },
];
function SidebarBrand({ onNavigate }: { onNavigate?: () => void }) {
  const { org } = useWorkspace();
  return (
    <Link to="/" onClick={onNavigate} className="workspace-brand">
      <LogoMark className="size-8" />
      <div>
        <strong>CallSync</strong>
        <span>{org.name}</span>
      </div>
    </Link>
  );
}
function Sidebar({ onNavigate, showBrand = true }: { onNavigate?: () => void; showBrand?: boolean }) {
  const manageId = useId();
  const { org, plan, user } = useWorkspace();
  const setup = useSetupProgress(undefined, false, false);
  const manageKey = `cs_try4_manage_open_${user.id}_${org.id}`;
  const [manageOpen, setManageOpen] = useState(() => storage.get<boolean>(manageKey, true));
  useEffect(() => setManageOpen(storage.get<boolean>(manageKey, true)), [manageKey]);
  const toggleManage = () => {
    const next = !manageOpen;
    setManageOpen(next);
    storage.set(manageKey, next);
  };
  return (
    <div className="sidebar-inner">
      {showBrand && <SidebarBrand onNavigate={onNavigate} />}
      <nav aria-label="Main navigation">
        {!org.onboardingCompleted && (
          <Link onClick={onNavigate} className="sidebar-setup" to={setup.next?.href || '/welcome'}>
            <div className="setup-top">
              <span>{setup.next?.cta || 'Finish setup'}</span>
              <strong>
                {setup.done}/{setup.total}
              </strong>
              <ChevronRight size={13} />
            </div>
            <Progress className="setup-meter" value={(setup.done / setup.total) * 100} />
          </Link>
        )}
        {NAV_SECTIONS.map((s) => (
          <div
            className={cn(
              'nav-group',
              s.title === 'Manage' && 'nav-manage',
              s.title === 'Account' && 'nav-account',
            )}
            key={s.title}
          >
            {s.title === 'Manage' ? (
              <button
                className="nav-item manage-toggle"
                aria-expanded={manageOpen}
                aria-controls={manageId}
                onClick={toggleManage}
              >
                <Users size={17} />
                <span>Manage</span>
                <ChevronDown size={14} className={cn('manage-chevron', manageOpen && 'rotate-180')} />
              </button>
            ) : null}
            <div
              id={s.title === 'Manage' ? manageId : undefined}
              hidden={s.title === 'Manage' && !manageOpen}
              className={s.title === 'Manage' ? 'manage-items' : undefined}
            >
              {s.items.map((n) => (
                <NavLink
                  onClick={onNavigate}
                  onMouseEnter={() => preloadPage(n.to)}
                  onFocus={() => preloadPage(n.to)}
                  key={n.to}
                  to={n.to}
                  end={'end' in n && n.end}
                  className={({ isActive }) => cn('nav-item', isActive && 'active')}
                >
                  <n.icon size={17} />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
        <NavLink
          onClick={onNavigate}
          className={({ isActive }) => cn('nav-item sidebar-help', isActive && 'active')}
          to="/help"
        >
          <HelpCircle size={17} />
          Help &amp; support
        </NavLink>
      </nav>
      <Link to="/billing" onClick={onNavigate} className="sidebar-plan-compact">
        <div className="sidebar-plan-status">
          <span className={cn('plan-dot', plan.isExpired && 'expired')} />
          <strong>{plan.isTrial ? 'Trial' : 'Upgraded'}</strong>
          <span>
            {plan.isExpired ? 'Expired' : plan.isTrial ? `${plan.daysLeft} days left` : 'Manage'}
            <ChevronRight size={12} />
          </span>
        </div>
        <p>
          {org.allowedUsers} phones ·{formatStorageGb(org.allowedStorageGb)}
        </p>
      </Link>
    </div>
  );
}
export function PageSkeleton() {
  return (
    <div className="page" role="status" aria-label="Loading page" aria-busy="true">
      <span className="sr-only">Loading page…</span>
      <div className="page-heading" aria-hidden="true">
        <div>
          <Skeleton className="h-[30px] w-40" />
          <Skeleton className="h-5 w-64 max-w-full mt-2" />
        </div>
        <Skeleton className="h-9 w-32" />
      </div>
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-[640px] w-full" />
    </div>
  );
}
export function AppShell() {
  const [mobile, setMobile] = useState(false);
  const [command, setCommand] = useState(false);
  const [installHelp, setInstallHelp] = useState(false);
  const installPrompt = useInstallPrompt();
  const { user, org, plan } = useWorkspace();
  const { signOut } = useAuth();
  const guardSignOut = useDraftGuard(false, false, '*');
  const { dark, setPref } = useTheme();
  const { track } = useAudio();
  const chat = useSupportChat();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => setMobile(false), [location.pathname]);
  useEffect(() => rememberSetupDone(org.onboardingCompleted), [org.onboardingCompleted]);
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommand((v) => !v);
      }
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, []);
  const current =
    NAV_SECTIONS.flatMap((s) => s.items).find((i) => i.to === location.pathname)?.label ||
    ({ '/help': 'Help' } as Record<string, string>)[location.pathname] ||
    'Workspace';
  return (
    <div className="app-frame">
      <aside className="app-sidebar">
        <Sidebar />
      </aside>
      <Drawer
        open={mobile}
        onClose={() => setMobile(false)}
        title="Main navigation"
        heading={<SidebarBrand onNavigate={() => setMobile(false)} />}
        className="navigation-drawer"
      >
        <Sidebar showBrand={false} onNavigate={() => setMobile(false)} />
      </Drawer>
      <div className="app-workspace">
        <header className="app-topbar">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Open navigation"
            className="lg:hidden"
            onClick={() => setMobile(true)}
          >
            <Menu />
          </Button>
          <div className="breadcrumb">
            <span>{org.name}</span>
            <span>/</span>
            <strong>{current}</strong>
          </div>
          <div className="topbar-actions">
            {config.isDemo && (
              <Suspense fallback={<Badge tone="neutral">Demo</Badge>}>
                <DemoScenarioSwitch />
              </Suspense>
            )}
            <button className="global-search" aria-label="Search dashboard" onClick={() => setCommand(true)}>
              <Search size={15} />
              <span>Search calls, contacts, pages</span>
              <kbd>⌘ K</kbd>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="account-trigger" aria-label="Account menu">
                  <Avatar name={user.name} seed={user.id} size="sm" />
                  <ChevronDown size={12} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <div className="px-3 py-2 text-xs">
                  <strong>{user.name}</strong>
                  <p className="mt-1 text-fg-muted">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem icon={<UserRound />} onSelect={() => navigate('/account')}>
                  Account
                </DropdownMenuItem>
                <DropdownMenuItem
                  icon={<MessageCircle />}
                  onSelect={() => (chat ? chat.open() : navigate('/help'))}
                >
                  Chat with support
                </DropdownMenuItem>
                <DropdownMenuItem
                  icon={dark ? <Sun /> : <Moon />}
                  onSelect={() => setPref(dark ? 'light' : 'dark')}
                >
                  {dark ? 'Light mode' : 'Dark mode'}
                </DropdownMenuItem>
                {!runningAsApp() && !installPrompt.installed && (
                  <DropdownMenuItem
                    icon={<MonitorDown />}
                    onSelect={() => (installPrompt.ready ? void installPrompt.open() : setInstallHelp(true))}
                  >
                    Install app
                  </DropdownMenuItem>
                )}
                {config.demoSwitchEnabled && (
                  <DropdownMenuItem
                    onSelect={() =>
                      guardSignOut(() => {
                        window.location.assign(
                          dataSourceUrl(window.location.href, config.isDemo ? 'supabase' : 'demo'),
                        );
                      })
                    }
                  >
                    {config.isDemo ? 'Switch to Live' : 'Switch to Demo'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem icon={<LogOut />} onSelect={() => guardSignOut(() => signOut())}>
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main id="main-content" tabIndex={-1} className={cn('workspace-main', track && 'has-player')}>
          {showPayNowBanner(plan) && (
            <div className="mb-5">
              <Notice tone="warning">
                Your trial ends {plan.daysLeft <= 1 ? 'tomorrow' : `in ${plan.daysLeft} days`}. Keep your calls and
                recordings syncing for {seatPriceLabel(billingCurrencyForCountry(org.billingAddress.country) ?? 'INR')} per
                phone per month.{' '}
                <Link className="font-medium underline" to="/billing">
                  Pay now
                </Link>
              </Notice>
            </div>
          )}
          {plan.isExpired && (
            <div className="mb-5">
              <Notice tone="warning">
                Your plan ended on {formatDate(org.planExpiry)}.{' '}
                <Link className="font-medium underline" to="/billing">
                  Renew to resume syncing.
                </Link>
              </Notice>
            </div>
          )}
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <ContactDrawer />
      <AudioBar />
      <CommandMenu open={command} onOpenChange={setCommand} />
      <InstallAppDialog open={installHelp} onClose={() => setInstallHelp(false)} />
    </div>
  );
}
